import "chart.js/auto";

import { Skeleton } from "@mui/material";
import classNames from "classnames";
import ControlFilter from "components/shared/ControlFilter";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import { useDateSelectorShortcuts } from "components/shared/DateRangeSelectors/utils";
import type { AnalyticsCumulatedSalesItem } from "helpers/parsers/parseConsolidatedSales";
import { SmallBodyHighlight, TinyBody } from "helpers/TypographyComponents";
import { isObjectEmpty } from "helpers/utils";
import useMounted from "hooks/useMounted";
import type { ChangeEvent, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { AnalyticsChartType, GroupMethod } from "store/analyticsStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { AnalyticsFilters, IDatesRange } from "types";

import type { FilterOption } from "../../../../components/shared/FiltersAutocomplete";
import FiltersAutocomplete from "../../../../components/shared/FiltersAutocomplete";
import styles from "../../style.module.scss";
import { useIsAnalyticsRefreshing } from "../../RefreshProgress/context";
import LoadingBlurFilter from "../LoadingBlurFilter";
import useSalesData from "../useSalesData";
import type { AnalyticsOptions, StoredPreferences } from "../utils";
import { ANALYTICS_DATE_PICKER_MAX_END, hydrateDates, settingsTransform } from "../utils";
import Chart from "./Chart";
import type { BarChartData, DataSet, LineChartData, PieChartData } from "./Chart/utils";
import { EmptyStateChart } from "./EmptyStateChart";
import { resolveAnalyticsDataType, useAnalyticsDataTypeOptions } from "../useAnalyticsDataTypeOptions";
import type { ChosenOptions } from "./utils";
import type { DataType } from "./utils";
import {
    formatDataSet,
    getIds,
    getMetricValue,
    plugMissingDates,
    sortColumnForDataType,
    useTagsLimit,
    viewOptions,
} from "./utils";

export enum ViewOption {
    TOTAL = 1,
    BY_TITLE = 2,
    BY_CHANNEL = 3,
    BY_COUNTRY = 4,
}

interface ISalesComparisonChart<U extends AnalyticsChartType> {
    /**
     * Label for Graph
     */
    label: string;
    /**
     * Label for type of data of a graph
     */
    dataTypeLabel: string;
    /**
     * Options for filters : titles, channels, country market
     */
    filterOptions: AnalyticsOptions;
    /**
     * Data type based on type of chart
     */
    type: U;
    /**
     * Company settings stored layout and filters preferences
     */
    storedPreferences: StoredPreferences;
    /**
     * Data for titles pagination from useAPIPagination hook
     */
    titlesPaginationData: {
        handleQueryTitle: (e: React.ChangeEvent<HTMLInputElement>) => void;
        setOffsetPaginationTitles: React.Dispatch<SetStateAction<number>>;
        requestStatusTitle: TFetchStatus;
    };
    /**
     * Periods date range
     */
    periodsDateRange: IDatesRange;
    /**
     * Specifies how data should be grouped
     */
    groupMethods: GroupMethod[];
}

const groupOtherValues = (data: AnalyticsCumulatedSalesItem[], dataType: DataType, threshold: number) => {
    const total = data.reduce((acc, item) => acc + getMetricValue(item, dataType), 0);

    let pointsCount = 0;

    return data.reduce<{ others: AnalyticsCumulatedSalesItem[]; main: AnalyticsCumulatedSalesItem[] }>(
        (acc, item) => {
            const isUnderThreshold = total > 0 && (100 * getMetricValue(item, dataType)) / total <= threshold;

            if (isUnderThreshold && pointsCount <= 6) {
                acc.others.push(item);
            } else {
                pointsCount++;
                acc.main.push(item);
            }

            return acc;
        },
        { others: [], main: [] },
    );
};

const postQueryGrouping = (
    data: AnalyticsCumulatedSalesItem[],
    groupMethod: GroupMethod,
    opts: {
        othersGroupingTreshold?: number;
        dataType: DataType;
    },
) => {
    const getGroupingKey = (item: AnalyticsCumulatedSalesItem) => {
        let propertyName: keyof AnalyticsCumulatedSalesItem | null = null;
        switch (groupMethod) {
            case "perChannel":
                propertyName = "channelName";
                break;
            case "perTitle":
                propertyName = "name";
                break;
            case "perCountry":
                propertyName = "countryMarket";
                break;
        }

        return propertyName ? item[propertyName] : undefined;
    };

    return data.reduce<Record<string, AnalyticsCumulatedSalesItem[]>>((acc, item) => {
        if (!getMetricValue(item, opts.dataType)) {
            return acc;
        }

        const groupingKey = getGroupingKey(item);

        if (groupingKey === undefined) {
            return acc;
        }

        if (!acc[groupingKey]) {
            acc[groupingKey] = [];
        }

        acc[groupingKey].push(item);

        return acc;
    }, {});
};

const SalesComparisonChart = <U extends AnalyticsChartType>({
    label,
    dataTypeLabel,
    filterOptions,
    type,
    storedPreferences,
    titlesPaginationData,
    periodsDateRange,
    groupMethods,
}: ISalesComparisonChart<U>) => {
    const mounted = useMounted(1000);
    const dispatch = useAppDispatch();
    const isRefreshing = useIsAnalyticsRefreshing();
    const { t } = useTranslation();
    const {
        chosenTitles: storedTitles,
        chosenChannels: storedChannels,
        chosenCountryMarkets: storedCountryMarkets,
        chosenDates: storedDates,
        dataType: storedDataType,
        viewOption: storedViewOption,
    } = storedPreferences?.storedFilters?.[type] || {};

    const { handleQueryTitle, setOffsetPaginationTitles, requestStatusTitle } = titlesPaginationData;

    const { refreshAnalyticsStatus } = useAppSelector((app) => app.analytics);

    const { periodsShortcuts } = useDateSelectorShortcuts();
    const { dataTypeOptions, subtitleByDataType, labelsReady } = useAnalyticsDataTypeOptions();

    const [chosenOptions, setChosenOptions] = useState<ChosenOptions>({
        chosenTitles: storedTitles || [],
        chosenChannels: storedChannels || [],
        chosenCountryMarkets: storedCountryMarkets || [],
        chosenDates: hydrateDates(storedDates) || periodsDateRange,
        dataType: (storedDataType as DataType) || "turnover",
        viewOption:
            storedViewOption || viewOptions(type as Extract<AnalyticsChartType, "line-chart" | "bar-chart">)?.[0],
    });

    const { chosenTitles, chosenChannels, chosenCountryMarkets, chosenDates, viewOption } = chosenOptions;
    const dataType = resolveAnalyticsDataType(chosenOptions.dataType, dataTypeOptions, labelsReady);

    const handleChooseOption = useCallback(
        (optionKey: string, value: any) => {
            if (optionKey === "viewOption") {
                value = viewOptions(type as "line-chart" | "bar-chart").find((option) => option.id === value);
            }
            setChosenOptions((prev) => ({ ...prev, [optionKey]: value }));
        },
        [type],
    );

    useEffect(() => {
        if (periodsDateRange?.startDate && isObjectEmpty(chosenDates)) {
            handleChooseOption("chosenDates", periodsDateRange);
        }
    }, [chosenDates, handleChooseOption, periodsDateRange]);

    const getLabel = useCallback(
        (
            item: AnalyticsCumulatedSalesItem,
            type: AnalyticsChartType,
            fallbackLabel: string,
            chosenGroup?: FilterOption["relatedOptionKey"],
        ): string => {
            switch (type) {
                case "pie-chart-per-channel":
                    return item.channelName || fallbackLabel;
                case "pie-chart-per-country":
                    return item.countryMarket || fallbackLabel;
                case "bar-chart":
                    if (chosenGroup === "chosenChannels") return item.channelName || fallbackLabel;
                    if (chosenGroup === "chosenTitles") return item.name;
                    if (chosenGroup === "chosenCountryMarkets") return item.countryMarket || fallbackLabel;
                    return t("Analytics.SumOfMenus");
                case "line-chart":
                    return item.date ? `${item.saleYear}-${`${item.saleMonth}`.padStart(2, "0")}` : fallbackLabel;
                default:
                    return fallbackLabel;
            }
        },
        [t],
    );

    const mergedGroupingMethods = useMemo(() => {
        const selectedGroupMethod = ((): GroupMethod | undefined => {
            switch (chosenOptions.viewOption?.relatedOptionKey) {
                case "chosenChannels":
                    return "perChannel";
                case "chosenTitles":
                    return "perTitle";
                case "chosenCountryMarkets":
                    return "perCountry";
                default:
                    if (type === "bar-chart") {
                        return "groupAll";
                    }
            }
        })();
        return [groupMethods, selectedGroupMethod || []].flat(1);
    }, [groupMethods, type, chosenOptions.viewOption?.relatedOptionKey]);

    const filters = useMemo<AnalyticsFilters>(() => {
        const { startDate, endDate } = chosenDates;
        const { titleIds, channelIds, countryCodes } = getIds(chosenTitles, chosenChannels, chosenCountryMarkets);

        return {
            dates: {
                startDate,
                endDate,
            },
            titleIds,
            channelIds,
            countryMarkets: countryCodes as string[],
            sortColumn: sortColumnForDataType(dataType),
            sortDirection: "desc",
        } satisfies AnalyticsFilters;
    }, [chosenChannels, chosenCountryMarkets, chosenDates, chosenTitles, dataType]);

    const { data, status, fetchNextPage } = useSalesData({
        filters: filters,
        groupMethods: mergedGroupingMethods,
    });

    const groupedData = useMemo(() => {
        if (!chosenOptions.viewOption?.relatedOptionKey || type === "bar-chart") {
            return { [t("Analytics.SumOfMenus")]: data };
        }

        const groupingOpts = {
            othersGroupingTreshold: 0.5,
            dataType: chosenOptions.dataType,
        };

        switch (chosenOptions.viewOption.relatedOptionKey) {
            case "chosenTitles":
                return postQueryGrouping(data, "perTitle", groupingOpts);
            case "chosenChannels":
                return postQueryGrouping(data, "perChannel", groupingOpts);
            case "chosenCountryMarkets":
                return postQueryGrouping(data, "perCountry", groupingOpts);
            default:
                return { "": data };
        }
    }, [chosenOptions.viewOption, data, type, chosenOptions.dataType, t]);

    useEffect(() => {
        if (isRefreshing) return;
        fetchNextPage();
    }, [filters, fetchNextPage, mergedGroupingMethods, isRefreshing]);

    /**
     * Save all options/filters in company settings
     */
    useEffect(() => {
        if (!mounted) return;

        const settingsValue = settingsTransform("filters", { ...chosenOptions, dataType }, storedPreferences, type);
        dispatch(putCompanySettings([{ settingsName: "analytics_layout", settingsValue }]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenOptions, dispatch, mounted, type]);

    const dataChart = useMemo<LineChartData | PieChartData | BarChartData | null>(() => {
        const dataSets = Object.entries(groupedData).reduce<
            Array<{
                label: string;
                data: Array<{ label: string; value: number }>;
                dataOthers: DataSet["dataOthers"];
            }>
        >((acc, [k, values]) => {
            if (!values?.length) {
                return acc;
            }

            const { others, main } = groupOtherValues(values, dataType, 0.5);

            const mainChartPoints = main
                .map((v) => {
                    return {
                        value: getMetricValue(v, dataType),
                        label: getLabel(v, type, t("Analytics.Undefined"), chosenOptions.viewOption?.relatedOptionKey),
                    };
                })
                .slice(0, 15);

            const othersChartPoints = others
                .map((v) => {
                    return {
                        value: getMetricValue(v, dataType),
                        label: getLabel(v, type, t("Analytics.Undefined"), chosenOptions.viewOption?.relatedOptionKey),
                    };
                })
                .sort((a, b) => b.value - a.value);

            if (othersChartPoints.length) {
                mainChartPoints.push({
                    label: t("Analytics.Others"),
                    value: othersChartPoints.reduce((acc, point) => acc + point.value, 0),
                });
            }

            const chartPoints = mainChartPoints.sort((a, b) => b.value - a.value);

            acc.push({
                label: k || "",
                data: chartPoints,
                dataOthers: othersChartPoints,
            });

            return acc;
        }, []);

        let chartLabels = Array.from(new Set(dataSets.map(({ data }) => data.map(({ label }) => label)).flat(1)));

        if (type === "line-chart") {
            dataSets.forEach((dataSet) => {
                dataSet.data = plugMissingDates(dataSet.data, chosenDates);
            });

            if (dataSets[0]?.data) {
                chartLabels = dataSets[0].data.map(({ label }) => label);
            }
        }

        return {
            labels: chartLabels,
            datasets: dataSets.map((dataSet, dataSetIndex) =>
                formatDataSet({
                    data: dataSet,
                    dataSetIndex,
                    chartType: type,
                }),
            ),
        };
    }, [groupedData, type, dataType, t, chosenOptions.viewOption?.relatedOptionKey, chosenDates, getLabel]);

    const isLoading = isRefreshing || refreshAnalyticsStatus === "pending" || status !== "succeeded";

    const handleChangeTitlesInput = (e: ChangeEvent<HTMLInputElement>) => {
        handleQueryTitle(e);
    };

    const handleScrollTitlesEnd = () => setOffsetPaginationTitles(filterOptions.titles?.length ?? 0);

    const resetTitleQuery = () => {
        handleQueryTitle({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
    };

    const unselectOption = (optionKey: string, optionId: number) => {
        setChosenOptions((prev) => {
            const updatedOption = prev[optionKey].filter((item) => item.id !== optionId);
            return { ...prev, [optionKey]: updatedOption };
        });
    };

    const tagsLimit = useTagsLimit(type, +viewOption?.id);

    return (
        <>
            {/** GRAPH LOADING FILTER */}
            {isLoading && <LoadingBlurFilter />}

            {/** GRAPH HEADER */}
            <div className={classNames({ [styles["graph-header"]]: true, [styles["loading"]]: isLoading })}>
                <SmallBodyHighlight color="black">{label}</SmallBodyHighlight>
                <div className={styles["date-range-filters"]}>
                    {!isObjectEmpty(chosenDates) && (
                        <DateRangeSelectors
                            label={t("Analytics.DataBetween")}
                            dateRange={periodsDateRange}
                            maxEndDate={ANALYTICS_DATE_PICKER_MAX_END}
                            defaultValues={chosenDates}
                            rangeShortcuts={periodsShortcuts}
                            handleChanges={(newDatesRange) => handleChooseOption("chosenDates", newDatesRange)}
                        />
                    )}
                </div>
            </div>

            {/** FILTERS */}
            <div className={classNames({ [styles["filters-wrapper"]]: true, [styles["loading"]]: isLoading })}>
                <FiltersAutocomplete
                    style={{ flex: 1 }}
                    label={<SmallBodyHighlight>{t("Analytics.TitlesFilter")}</SmallBodyHighlight>}
                    placeholder={t("Analytics.AllTitles")}
                    filtersOptions={filterOptions.titles}
                    chosenOptions={chosenTitles}
                    handleChooseOption={(value) => handleChooseOption("chosenTitles", value)}
                    unselectOption={(optionId) => unselectOption("chosenTitles", optionId)}
                    onInputChange={handleChangeTitlesInput}
                    onScrollEnd={handleScrollTitlesEnd}
                    onScrollEndLoading={requestStatusTitle}
                    limitTags={tagsLimit.titles}
                    onClose={() => resetTitleQuery()}
                    showOptionAll
                    optionAllName={t("Analytics.AllTitles")}
                    disableCloseOnSelect
                    size="small"
                />
                <FiltersAutocomplete
                    style={{ flex: 1 }}
                    label={<SmallBodyHighlight>{t("Analytics.ChannelsFilter")}</SmallBodyHighlight>}
                    placeholder={t("Analytics.AllChannels")}
                    filtersOptions={filterOptions.channels}
                    chosenOptions={chosenChannels}
                    handleChooseOption={(value) => handleChooseOption("chosenChannels", value)}
                    unselectOption={(optionId) => unselectOption("chosenChannels", optionId)}
                    limitTags={tagsLimit.channels}
                    disableCloseOnSelect
                    showOptionAll
                    optionAllName={t("Analytics.AllChannels")}
                    size="small"
                />
                <FiltersAutocomplete
                    style={{ flex: 1 }}
                    label={<SmallBodyHighlight>{t("Analytics.CountriesFilter")}</SmallBodyHighlight>}
                    placeholder={t("Analytics.AllCountries")}
                    filtersOptions={filterOptions.countryMarkets}
                    chosenOptions={chosenCountryMarkets}
                    handleChooseOption={(value) => handleChooseOption("chosenCountryMarkets", value)}
                    unselectOption={(optionId) => unselectOption("chosenCountryMarkets", optionId)}
                    limitTags={tagsLimit.countryMarkets}
                    disableCloseOnSelect
                    showOptionAll
                    optionAllName={t("Analytics.AllCountries")}
                    size="small"
                />
            </div>

            {/** CHARTS */}
            <div className={styles["graph-wrapper"]}>
                {dataChart && !isLoading && <Chart type={type} dataType={dataType} dataChart={dataChart} />}
                {isLoading && <Skeleton className={styles["graph-skeleton"]} />}
                {!dataChart && !isLoading && <EmptyStateChart />}
            </div>

            {/** GRAPH FOOTER */}
            <div className={styles["graph-footer"]}>
                <TinyBody color="grey">
                    {dataTypeLabel} {subtitleByDataType[dataType]}
                </TinyBody>
                <div className={styles["graph-footer-right"]}>
                    <TinyBody color="grey" style={{ marginRight: "0.25rem" }}>
                        {t("Analytics.Dimension")}
                    </TinyBody>
                    <ControlFilter
                        controlValue={dataType}
                        filterOptions={dataTypeOptions}
                        onValueChange={(value) => handleChooseOption("dataType", value)}
                    />
                    {["line-chart", "bar-chart"].includes(type) && (
                        <>
                            <TinyBody color="grey" style={{ marginRight: "0.25rem", marginLeft: "1rem" }}>
                                {t("Analytics.Cumulative")}
                            </TinyBody>
                            <ControlFilter
                                controlValue={viewOption.value}
                                filterOptions={viewOptions(type as "line-chart" | "bar-chart")}
                                onValueChange={(option) => handleChooseOption("viewOption", option)}
                                selector
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default React.memo(SalesComparisonChart);
